<template>
  <div class="use-existing-equipment">
    <div class="button-container">
      <div class="button-row">
        <Button
          data-cy="use-existing-equipment-button"
          label="Use Existing Equipment"
          icon="pi pi-copy"
          @click="openDialog"
        />
      </div>
      <!-- Color Legend -->
      <div class="color-legend">
        <div class="legend-item">
          <div class="legend-color lower-color"></div>
          <span class="legend-text">Lower Stack</span>
        </div>
        <div class="legend-item">
          <div class="legend-color upper-color"></div>
          <span class="legend-text">Upper Stack</span>
        </div>
      </div>
    </div>
    <Dialog
      v-model:visible="showDialog"
      modal
      header="Copy Equipment from Upper to Lower"
      :style="{ width: '500px' }"
    >
      <div class="dialog-description">
        <p class="description-text">
          Select the equipment types you want to copy from the
          <strong>Upper Stack</strong> to the <strong>Lower Stack</strong>.
        </p>
      </div>
      <div class="copy-options">
        <div class="option-item">
          <Checkbox
            v-model="selectedTypes.intake"
            input-id="copy-intake"
            :binary="true"
          />
          <label for="copy-intake">Intake</label>
        </div>
        <div class="option-item">
          <Checkbox
            v-model="selectedTypes.pump"
            input-id="copy-pump"
            :binary="true"
          />
          <label for="copy-pump">Pump</label>
        </div>
        <div class="option-item">
          <Checkbox
            v-model="selectedTypes.motor"
            input-id="copy-motor"
            :binary="true"
          />
          <label for="copy-motor">Motor</label>
        </div>
        <div class="option-item">
          <Checkbox
            v-model="selectedTypes.seal"
            input-id="copy-seal"
            :binary="true"
          />
          <label for="copy-seal">Seal</label>
        </div>
        <div class="option-item">
          <Checkbox
            v-model="selectedTypes.cable"
            input-id="copy-cable"
            :binary="true"
          />
          <label for="copy-cable">Cable</label>
        </div>
        <div class="option-item">
          <Checkbox
            v-model="selectedTypes.mle"
            input-id="copy-mle"
            :binary="true"
          />
          <label for="copy-mle">MLE</label>
        </div>
        <div class="option-item">
          <Checkbox
            v-model="selectedTypes.sensor"
            input-id="copy-sensor"
            :binary="true"
          />
          <label for="copy-sensor">Sensor</label>
        </div>
        <div class="option-item">
          <Checkbox
            v-model="selectedTypes.sealMotorOil"
            input-id="copy-seal-motor-oil"
            :binary="true"
          />
          <label for="copy-seal-motor-oil">Seal Motor Oil</label>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer-content">
          <div class="footer-left">
            <Button
              data-cy="select-all-button"
              label="Select All"
              icon="pi pi-check-square"
              class="p-button-text p-button-sm"
              @click="selectAll"
            />
            <Button
              data-cy="deselect-all-button"
              label="Deselect All"
              icon="pi pi-times-circle"
              class="p-button-text p-button-sm"
              @click="deselectAll"
            />
          </div>
          <div class="footer-right">
            <Button
              data-cy="copy-dialog-cancel-button"
              label="Cancel"
              icon="pi pi-times"
              class="p-button-text"
              @click="showDialog = false"
            />
            <Button
              data-cy="copy-dialog-copy-button"
              label="Copy"
              icon="pi pi-check"
              :disabled="!hasSelection"
              @click="handleCopy"
            />
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { v4 } from "uuid";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Checkbox from "primevue/checkbox";
import { injectStrict, roundFloat } from "@/utils/utils";
import {
  equipmentFormKey,
  intakesFormKey,
  pumpsFormKey,
  sealsFormKey,
  cablesFormKey,
  housingsFormKey,
  parametersFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { LengthUnits } from "@/libs/units/length";
import { Override_Options_Enum } from "gql/api";
import type {
  XrefHousings,
  XrefIntakes,
  XrefPumps,
  XrefSeals,
  XrefCables,
  OperatingParameters
} from "@/composables/forms/formTypes";
import { useGenericNotification } from "@/composables/logger/genericNotification";
import {
  getFieldName,
  getPumpFieldName,
  getMotorFieldName,
  getPumpFieldValue,
  getMotorFieldValue,
  getFieldValue,
  type PumpFieldKey,
  type MotorFieldKey,
  type GasSeparationFieldKey
} from "@/composables/forms/stackAwareFields";

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const intakesForm = injectStrict(intakesFormKey);
const pumpsForm = injectStrict(pumpsFormKey);
const sealsForm = injectStrict(sealsFormKey);
const cablesForm = injectStrict(cablesFormKey);
const housingsForm = injectStrict(housingsFormKey);
const parametersForm = injectStrict(parametersFormKey);

const { fireToast } = useGenericNotification();

const showDialog = ref(false);
const selectedTypes = ref({
  intake: false,
  pump: false,
  motor: false,
  seal: false,
  cable: false,
  mle: false,
  sensor: false,
  sealMotorOil: false
});

function openDialog() {
  selectAll();
  showDialog.value = true;
}

const hasSelection = computed(() => {
  return Object.values(selectedTypes.value).some(v => v);
});

function selectAll() {
  Object.keys(selectedTypes.value).forEach(key => {
    selectedTypes.value[key as keyof typeof selectedTypes.value] = true;
  });
}

function deselectAll() {
  Object.keys(selectedTypes.value).forEach(key => {
    selectedTypes.value[key as keyof typeof selectedTypes.value] = false;
  });
}

// Fields to exclude: only journal fields (not r_* relationships)
const fieldsToExclude = [
  "journal_author_id",
  "journal_created_at",
  "journal_updated_at",
  "journal_deleted_at",
  "winning_revision_author_id",
  "parent_id"
] as const;

/**
 * Filters out journal fields from equipment xref objects.
 * Journal fields are dynamically set later in the pipeline and should not be copied.
 * Note: r_* fields (read-only GraphQL relationships) are preserved as they contain
 * display data that should remain in the form (e.g., r_intake with intake_type).
 */
function excludeJournalFields<T extends Record<string, unknown>>(
  obj: T
): Omit<
  T,
  | "journal_author_id"
  | "journal_created_at"
  | "journal_updated_at"
  | "journal_deleted_at"
  | "winning_revision_author_id"
  | "parent_id"
> {
  const filtered = Object.keys(obj).reduce(
    (acc, key) => {
      if (!fieldsToExclude.some(field => field === key)) {
        acc[key] = obj[key];
      }
      return acc;
    },
    {} as Record<string, unknown>
  );

  return filtered as Omit<
    T,
    | "journal_author_id"
    | "journal_created_at"
    | "journal_updated_at"
    | "journal_deleted_at"
    | "winning_revision_author_id"
    | "parent_id"
  >;
}

/**
 * Creates an object with journal fields explicitly set to null.
 * Used when copying equipment to ensure journal fields don't appear in diffs.
 */
function setJournalFieldsToNull<T extends Record<string, unknown>>(
  obj: T
): T & {
  journal_author_id: null;
  journal_created_at: null;
  journal_updated_at: null;
  journal_deleted_at: null;
} {
  const result = { ...obj };
  fieldsToExclude.forEach(field => {
    if (field.startsWith("journal_")) {
      (result as Record<string, unknown>)[field] = null;
    }
  });
  return result as T & {
    journal_author_id: null;
    journal_created_at: null;
    journal_updated_at: null;
    journal_deleted_at: null;
  };
}

/**
 * Calculates total weight from housings
 */
function getAccumulatedOrderedHousingWeight(
  housingIds: Array<string> | undefined
): number {
  return (
    housingIds?.reduce((totalWeight: number, housingId: string) => {
      const housing = housingsForm.data.value[housingId];
      return totalWeight + (housing?.r_housing?.weight_pounds || 0);
    }, 0) || 0
  );
}

/**
 * Calculates total length from housings
 */
function getAccumulatedOrderedHousingLength(
  housingIds: Array<string> | undefined
): number {
  return (
    housingIds?.reduce((totalLength: number, housingId: string) => {
      const housing = housingsForm.data.value[housingId];
      const lengthInches = housing?.length_inches || 0;
      return totalLength + LengthUnits.Inches.toStandardUnit(lengthInches);
    }, 0) || 0
  );
}

/**
 * Calculates total stages from housings
 */
function getAccumulatedOrderedHousingStages(
  housingIds: Array<string> | undefined
): number {
  return (
    housingIds?.reduce((totalStages: number, housingId: string) => {
      const housing = housingsForm.data.value[housingId];
      return totalStages + (housing?.stage_count || 0);
    }, 0) || 0
  );
}

/**
 * Copies a stack-aware parameter field from upper to lower stack.
 * Only updates if the value is different to avoid unnecessary diffs.
 * @param params - The parameters form data
 * @param field - The logical field name (e.g., "pumpHz", "motorRerateRatio")
 * @param getFieldNameFn - Function to get the actual field name based on stack
 * @param getFieldValueFn - Function to get the field value from params
 * @param updates - Object to collect updates
 */
function copyStackAwareField<T extends PumpFieldKey | MotorFieldKey>(
  params: OperatingParameters | null | undefined,
  field: T,
  getFieldNameFn: (field: T, stack?: "upper" | "lower") => string,
  getFieldValueFn: (
    params: OperatingParameters | null | undefined,
    field: T,
    stack?: "upper" | "lower"
  ) => unknown,
  updates: Record<string, unknown>
): void {
  const lowerFieldName = getFieldNameFn(field, "lower");
  const upperValue = getFieldValueFn(params, field, "upper");
  const lowerValue = getFieldValueFn(params, field, "lower");

  if (upperValue !== undefined && upperValue !== lowerValue) {
    updates[lowerFieldName] = upperValue;
  }
}

/**
 * Copies a gas separation field from upper to lower stack.
 * Handles special logic for Override_Options_Enum fields.
 */
function copyGasSeparationField(
  params: OperatingParameters | null | undefined,
  overrideTypeField: GasSeparationFieldKey,
  percentField: GasSeparationFieldKey,
  updates: Record<string, unknown>
): void {
  const upperOverrideType = getFieldValue(
    params,
    overrideTypeField,
    "upper"
  ) as Override_Options_Enum | undefined;
  const upperPercent = getFieldValue(params, percentField, "upper") as
    | number
    | null
    | undefined;

  if (upperOverrideType !== undefined) {
    const lowerOverrideTypeName = getFieldName(overrideTypeField, "lower");
    const lowerPercentFieldName = getFieldName(percentField, "lower");

    if (upperOverrideType === Override_Options_Enum.Calculate) {
      updates[lowerOverrideTypeName] = Override_Options_Enum.Calculate;
      // TODO: When lower stack calculations are implemented, this will automatically use calculated values
      // The percent field value (null) is a placeholder - Calculate mode uses computed properties, not this value
      updates[lowerPercentFieldName] = null;
    } else if (upperOverrideType === Override_Options_Enum.Override) {
      updates[lowerOverrideTypeName] = Override_Options_Enum.Override;
      // Convert 0 to null when copying
      updates[lowerPercentFieldName] = upperPercent === 0 ? null : upperPercent;
    } else if (upperOverrideType === Override_Options_Enum.Disable) {
      updates[lowerOverrideTypeName] = Override_Options_Enum.Disable;
      updates[lowerPercentFieldName] = null;
    }
  }
}

function handleCopy() {
  const updates: Partial<typeof equipment.value> = {};
  const parameters = parametersForm.data.value;

  // Copy Intakes
  if (selectedTypes.value.intake && equipment.value.intakes_order) {
    const upperOrder = equipment.value.intakes_order as string[];
    const lowerOrder: string[] = [];
    const intakeXrefs: Record<string, XrefIntakes[number]> = {};
    const lowerIntakesOrder =
      (equipment.value.lower_intakes_order as string[]) || [];

    // Build a map of existing lower intakes by intake_id for quick lookup
    const existingLowerIntakesByIntakeId = new Map<string, string>();
    lowerIntakesOrder.forEach(lowerId => {
      const lowerIntake = intakesForm.data.value[lowerId];
      if (
        lowerIntake &&
        !lowerIntake.journal_deleted_at &&
        lowerIntake.intake_id
      ) {
        existingLowerIntakesByIntakeId.set(lowerIntake.intake_id, lowerId);
      }
    });

    upperOrder.forEach(upperId => {
      const upperIntake = intakesForm.data.value[upperId];
      if (upperIntake && !upperIntake.journal_deleted_at) {
        // Check if lower stack already has an intake with the same intake_id
        const existingLowerId =
          upperIntake.intake_id &&
          existingLowerIntakesByIntakeId.get(upperIntake.intake_id);

        if (existingLowerId) {
          // Reuse existing lower intake - no changes needed, just add to order
          lowerOrder.push(existingLowerId);
        } else {
          // Create new intake xref
          const newId = v4();
          lowerOrder.push(newId);
          // Copy all xref fields explicitly, excluding journal fields.
          // Note: r_intake is a read-only GraphQL join that gets resolved by the backend
          // based on intake_id, but we preserve it in the form data for display purposes
          intakeXrefs[newId] = {
            ...excludeJournalFields(upperIntake),
            id: newId,
            winning_revision_id: null,
            winning_revision_parent_id: null,
            winning_revision_created_at: "now()",
            winning_revision_updated_at: "now()",
            winning_revision_deleted_at: null
          };
        }
      }
    });

    // Only update if there are new intakes to add or order changed
    if (Object.keys(intakeXrefs).length > 0) {
      intakesForm.update(intakeXrefs);
    }
    updates.lower_intakes_order = lowerOrder;

    // Collect all parameter updates to batch them
    const parameterUpdates: Record<string, unknown> = {};

    // Copy packer field from upper to lower
    const upperPackerInstalled = getFieldValue(
      parameters,
      "packerInstalled",
      "upper"
    ) as boolean | undefined;
    if (upperPackerInstalled !== undefined) {
      parameterUpdates[getFieldName("packerInstalled", "lower")] =
        upperPackerInstalled;
    }

    // Copy natural gas separation override fields from upper to lower
    copyGasSeparationField(
      parameters,
      "naturalGasSepOverrideType",
      "naturalGasSepPercent",
      parameterUpdates
    );

    // Copy intake gas separation override fields from upper to lower
    copyGasSeparationField(
      parameters,
      "intakeGasSepOverrideType",
      "intakeGasSepPercent",
      parameterUpdates
    );

    // Batch all parameter updates into a single call
    if (Object.keys(parameterUpdates).length > 0) {
      parametersForm.update(parameterUpdates);
    }
  }

  // Copy Pumps
  if (selectedTypes.value.pump && equipment.value.pumps_order) {
    const upperOrder = equipment.value.pumps_order as string[];
    const lowerOrder: string[] = [];
    const pumpXrefs: Record<string, XrefPumps[number]> = {};
    const lowerPumpsOrder =
      (equipment.value.lower_pumps_order as string[]) || [];

    // Build a map of existing lower pumps by pump_id for quick lookup
    const existingLowerPumpsByPumpId = new Map<string, string>();
    lowerPumpsOrder.forEach(lowerId => {
      const lowerPump = pumpsForm.data.value[lowerId];
      if (lowerPump && !lowerPump.journal_deleted_at && lowerPump.pump_id) {
        existingLowerPumpsByPumpId.set(lowerPump.pump_id, lowerId);
      }
    });

    upperOrder.forEach(upperId => {
      const upperPump = pumpsForm.data.value[upperId];
      if (upperPump && !upperPump.journal_deleted_at) {
        // Check if lower stack already has a pump with the same pump_id
        const existingLowerId =
          upperPump.pump_id &&
          existingLowerPumpsByPumpId.get(upperPump.pump_id);

        if (existingLowerId) {
          // Reuse existing lower pump - check if housings need to be copied
          const existingLowerPump = pumpsForm.data.value[existingLowerId];
          const upperHousingsOrder = upperPump.housings_order || [];
          const existingHousingsOrder = existingLowerPump?.housings_order || [];

          // Copy housings if upper pump has housings and lower pump doesn't have them
          // or if the housing orders differ (indicating different housings)
          if (
            upperHousingsOrder.length > 0 &&
            (existingHousingsOrder.length === 0 ||
              JSON.stringify(existingHousingsOrder) !==
                JSON.stringify(upperHousingsOrder))
          ) {
            const pumpHousings: Record<string, XrefHousings[number]> = {};
            const newHousingOrder: string[] = [];

            upperHousingsOrder.forEach((housingId: string) => {
              const upperHousing = housingsForm.data.value[housingId];
              if (upperHousing && !upperHousing.journal_deleted_at) {
                const newHousingId = v4();
                newHousingOrder.push(newHousingId);
                pumpHousings[newHousingId] = {
                  ...setJournalFieldsToNull(excludeJournalFields(upperHousing)),
                  id: newHousingId,
                  scenario_equipment_xref_pump_id: existingLowerId, // Link to existing lower pump
                  winning_revision_id: null,
                  winning_revision_parent_id: null,
                  winning_revision_created_at: "now()",
                  winning_revision_updated_at: "now()",
                  winning_revision_deleted_at: null
                };
              }
            });

            // Update housings form if we created new housings
            if (Object.keys(pumpHousings).length > 0) {
              housingsForm.update(pumpHousings);
              // Recalculate pump values from housings
              const updatedWeight =
                getAccumulatedOrderedHousingWeight(newHousingOrder);
              const updatedLength =
                getAccumulatedOrderedHousingLength(newHousingOrder);
              const updatedStages =
                getAccumulatedOrderedHousingStages(newHousingOrder);

              // Update the existing lower pump's housings_order and recalculated values
              pumpsForm.update({
                [existingLowerId]: {
                  ...existingLowerPump,
                  housings_order: newHousingOrder,
                  weight_pounds: updatedWeight,
                  length_feet: roundFloat(updatedLength),
                  total_stages: updatedStages
                }
              });
            }
          }
          lowerOrder.push(existingLowerId);
        } else {
          // Create new pump xref
          const newId = v4();
          lowerOrder.push(newId);

          // Copy pump housings if they exist
          const pumpHousings: Record<string, XrefHousings[number]> = {};
          if (upperPump.housings_order) {
            upperPump.housings_order.forEach((housingId: string) => {
              const upperHousing = housingsForm.data.value[housingId];
              if (upperHousing && !upperHousing.journal_deleted_at) {
                const newHousingId = v4();
                pumpHousings[newHousingId] = {
                  ...setJournalFieldsToNull(excludeJournalFields(upperHousing)),
                  id: newHousingId,
                  scenario_equipment_xref_pump_id: newId, // CRITICAL: Link to new pump
                  winning_revision_id: null,
                  winning_revision_parent_id: null,
                  winning_revision_created_at: "now()",
                  winning_revision_updated_at: "now()",
                  winning_revision_deleted_at: null
                };
              }
            });
          }

          const newHousingOrder = Object.keys(pumpHousings);
          pumpXrefs[newId] = {
            ...setJournalFieldsToNull(excludeJournalFields(upperPump)),
            id: newId,
            housings_order: newHousingOrder,
            // Recalculate pump values from housings
            weight_pounds: getAccumulatedOrderedHousingWeight(newHousingOrder),
            length_feet: roundFloat(
              getAccumulatedOrderedHousingLength(newHousingOrder)
            ),
            total_stages: getAccumulatedOrderedHousingStages(newHousingOrder),
            winning_revision_id: null,
            winning_revision_parent_id: null,
            winning_revision_created_at: "now()",
            winning_revision_updated_at: "now()",
            winning_revision_deleted_at: null
          };

          if (Object.keys(pumpHousings).length > 0) {
            housingsForm.update(pumpHousings);
          }
        }
      }
    });

    // Only update if there are new pumps to add or order changed
    if (Object.keys(pumpXrefs).length > 0) {
      pumpsForm.update(pumpXrefs);
    }
    updates.lower_pumps_order = lowerOrder;

    // Collect pump parameter updates to batch them
    const pumpParameterUpdates: Record<string, unknown> = {};

    // Copy all pump parameter fields from upper to lower using stack-aware helpers
    copyStackAwareField(
      parameters,
      "pumpHz",
      getPumpFieldName,
      getPumpFieldValue,
      pumpParameterUpdates
    );
    copyStackAwareField(
      parameters,
      "hasCompressionPump",
      getPumpFieldName,
      getPumpFieldValue,
      pumpParameterUpdates
    );
    copyStackAwareField(
      parameters,
      "includeCasingFrictionLoss",
      getPumpFieldName,
      getPumpFieldValue,
      pumpParameterUpdates
    );
    copyStackAwareField(
      parameters,
      "pumpOverrideGasCorrectionPercent",
      getPumpFieldName,
      getPumpFieldValue,
      pumpParameterUpdates
    );
    copyStackAwareField(
      parameters,
      "pumpWearHeadCorrectionRatio",
      getPumpFieldName,
      getPumpFieldValue,
      pumpParameterUpdates
    );
    copyStackAwareField(
      parameters,
      "pumpWearBhpCorrectionRatio",
      getPumpFieldName,
      getPumpFieldValue,
      pumpParameterUpdates
    );
    copyStackAwareField(
      parameters,
      "useLegacyGasHeadRerate",
      getPumpFieldName,
      getPumpFieldValue,
      pumpParameterUpdates
    );

    // Batch pump parameter updates into a single call
    if (Object.keys(pumpParameterUpdates).length > 0) {
      parametersForm.update(pumpParameterUpdates);
    }
  }

  // Copy Seals
  if (selectedTypes.value.seal && equipment.value.seals_order) {
    const upperOrder = equipment.value.seals_order as string[];
    const lowerOrder: string[] = [];
    const sealXrefs: Record<string, XrefSeals[number]> = {};
    const lowerSealsOrder =
      (equipment.value.lower_seals_order as string[]) || [];

    // Build a map of existing lower seals by seal_id for quick lookup
    // Check all seals in the form, not just those in order (in case order is out of sync)
    const existingLowerSealsBySealId = new Map<string, string>();

    // First, check seals in the current order
    lowerSealsOrder.forEach(lowerId => {
      const lowerSeal = sealsForm.data.value[lowerId];
      if (lowerSeal && !lowerSeal.journal_deleted_at && lowerSeal.seal_id) {
        existingLowerSealsBySealId.set(lowerSeal.seal_id, lowerId);
      }
    });

    // Also check all seals in the form that are lower stack seals
    Object.keys(sealsForm.data.value).forEach(sealId => {
      const seal = sealsForm.data.value[sealId];
      if (
        seal &&
        !seal.journal_deleted_at &&
        seal.seal_id &&
        !existingLowerSealsBySealId.has(seal.seal_id) &&
        !upperOrder.includes(sealId) // Not in upper order, so lower stack
      ) {
        existingLowerSealsBySealId.set(seal.seal_id, sealId);
      }
    });

    upperOrder.forEach(upperId => {
      const upperSeal = sealsForm.data.value[upperId];
      if (upperSeal && !upperSeal.journal_deleted_at) {
        // Check if lower stack already has a seal with the same seal_id
        const existingLowerId =
          upperSeal.seal_id &&
          existingLowerSealsBySealId.get(upperSeal.seal_id);

        if (existingLowerId) {
          // Reuse existing lower seal - no changes needed, just add to order
          lowerOrder.push(existingLowerId);
        } else {
          // Create new seal xref
          const newId = v4();
          lowerOrder.push(newId);
          sealXrefs[newId] = {
            ...excludeJournalFields(upperSeal),
            id: newId,
            winning_revision_id: null,
            winning_revision_parent_id: null,
            winning_revision_created_at: "now()",
            winning_revision_updated_at: "now()",
            winning_revision_deleted_at: null
          };
        }
      }
    });

    // Only update if there are new seals to add or order changed
    if (Object.keys(sealXrefs).length > 0) {
      sealsForm.update(sealXrefs);
    }
    updates.lower_seals_order = lowerOrder;
  }

  // Copy Cables
  if (selectedTypes.value.cable && equipment.value.cables_order) {
    const upperOrder = equipment.value.cables_order as string[];
    const lowerOrder: string[] = [];
    const cableXrefs: Record<string, XrefCables[number]> = {};
    const lowerCablesOrder =
      (equipment.value.lower_cables_order as string[]) || [];

    // Build a map of existing lower cables by cable_id for quick lookup
    const existingLowerCablesByCableId = new Map<string, string>();
    lowerCablesOrder.forEach(lowerId => {
      const lowerCable = cablesForm.data.value[lowerId];
      if (lowerCable && !lowerCable.journal_deleted_at && lowerCable.cable_id) {
        existingLowerCablesByCableId.set(lowerCable.cable_id, lowerId);
      }
    });

    upperOrder.forEach(upperId => {
      const upperCable = cablesForm.data.value[upperId];
      if (upperCable && !upperCable.journal_deleted_at) {
        // Check if lower stack already has a cable with the same cable_id
        const existingLowerId =
          upperCable.cable_id &&
          existingLowerCablesByCableId.get(upperCable.cable_id);

        if (existingLowerId) {
          // Reuse existing lower cable - no changes needed, just add to order
          lowerOrder.push(existingLowerId);
        } else {
          // Create new cable xref
          const newId = v4();
          lowerOrder.push(newId);
          cableXrefs[newId] = {
            ...excludeJournalFields(upperCable),
            id: newId,
            winning_revision_id: null,
            winning_revision_parent_id: null,
            winning_revision_created_at: "now()",
            winning_revision_updated_at: "now()",
            winning_revision_deleted_at: null
          };
        }
      }
    });

    // Only update if there are new cables to add or order changed
    if (Object.keys(cableXrefs).length > 0) {
      cablesForm.update(cableXrefs);
    }
    updates.lower_cables_order = lowerOrder;
  }

  // Copy Motor
  if (selectedTypes.value.motor && equipment.value.motor_id) {
    // Only update if the value is different to avoid unnecessary diffs
    if (equipment.value.lower_motor_id !== equipment.value.motor_id) {
      updates.lower_motor_id = equipment.value.motor_id;
      // Copy r_motor relationship (read-only GraphQL join) to maintain motor type and all related fields
      if (equipment.value.r_motor) {
        updates.r_lower_motor = equipment.value.r_motor;
      }
    }

    // Collect motor parameter updates to batch them
    const motorParameterUpdates: Record<string, unknown> = {};

    // Copy all motor parameter fields from upper to lower using stack-aware helpers
    copyStackAwareField(
      parameters,
      "motorRerateRatio",
      getMotorFieldName,
      getMotorFieldValue,
      motorParameterUpdates
    );

    // Batch motor parameter updates into a single call
    if (Object.keys(motorParameterUpdates).length > 0) {
      parametersForm.update(motorParameterUpdates);
    }
  }

  // Copy MLE
  if (selectedTypes.value.mle && equipment.value.mle_id) {
    // Only update if the value is different to avoid unnecessary diffs
    if (equipment.value.lower_mle_id !== equipment.value.mle_id) {
      updates.lower_mle_id = equipment.value.mle_id;
      // Copy r_mle relationship (read-only GraphQL join) to maintain MLE type and all related fields
      if (equipment.value.r_mle) {
        updates.r_lower_mle = equipment.value.r_mle;
      }
    }
  }

  // Copy Sensor
  if (selectedTypes.value.sensor && equipment.value.sensor_id) {
    // Only update if the value is different to avoid unnecessary diffs
    if (equipment.value.lower_sensor_id !== equipment.value.sensor_id) {
      updates.lower_sensor_id = equipment.value.sensor_id;
      // Copy r_sensor relationship (read-only GraphQL join) to maintain sensor type and all related fields
      if (equipment.value.r_sensor) {
        updates.r_lower_sensor = equipment.value.r_sensor;
      }
    }
  }

  // Copy Seal Motor Oil
  if (selectedTypes.value.sealMotorOil && equipment.value.seal_motor_oil_id) {
    // Only update if the value is different to avoid unnecessary diffs
    if (
      equipment.value.lower_seal_motor_oil_id !==
      equipment.value.seal_motor_oil_id
    ) {
      updates.lower_seal_motor_oil_id = equipment.value.seal_motor_oil_id;
      // Copy r_seal_motor_oil relationship (read-only GraphQL join) to maintain seal motor oil type and all related fields
      if (equipment.value.r_seal_motor_oil) {
        updates.r_lower_seal_motor_oil = equipment.value.r_seal_motor_oil;
      }
    }
  }

  // Apply all updates
  if (Object.keys(updates).length > 0) {
    equipmentForm.update(updates);
    fireToast({
      severity: "success",
      summary: "Equipment Copied",
      detail: "Upper equipment has been copied to lower stack.",
      life: 3000
    });
  }

  showDialog.value = false;
  // Reset selections
  Object.keys(selectedTypes.value).forEach(key => {
    selectedTypes.value[key as keyof typeof selectedTypes.value] = false;
  });
}
</script>

<style lang="scss" scoped>
.use-existing-equipment {
  display: inline-block;
}

.button-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
}

.button-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.button-note {
  font-size: 0.875rem;
  color: var(--gray-600);
  font-style: italic;
}

.color-legend {
  display: flex;
  gap: 1.5rem;
  padding: 0.5rem 0.75rem;
  background-color: var(--surface-ground);
  border-radius: 6px;
  border: 1px solid var(--surface-border);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid;
}

.upper-color {
  background-color: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6; // Blue-500
}

.lower-color {
  background-color: rgba(16, 185, 129, 0.1);
  border-color: #10b981; // Green-500
}

.legend-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color);
}

.dialog-description {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: var(--blue-50);
  border-left: 3px solid var(--primary-color);
  border-radius: 4px;
}

.description-text {
  margin: 0;
  font-size: 0.9rem;
  color: var(--gray-700);
  line-height: 1.5;
}

.copy-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 0;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  label {
    cursor: pointer;
    user-select: none;
  }
}

:deep(.p-dialog-footer) {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--surface-border);
  background: var(--surface-ground);
}

.dialog-footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.footer-left {
  display: flex;
  gap: 0.5rem;
}

.footer-right {
  display: flex;
  gap: 0.5rem;
}
</style>
